'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { farsiLabels } from '@/types/brokers/slug';
import { exchangeFarsiLabels } from '@/types/exchanges/page';
import dynamic from 'next/dynamic';
import { FarsiSupportIcon, RialSupportIcon } from '@/components/Icons';
import {
    PercentageSquare,
    Bitcoin,
    Candle,
    Moneys,
    Calendar,
    Category,
    Map,
    TickCircle,
    CloseCircle,
    InfoCircle,
} from 'iconsax-react';
import { BrokerFields, CustomFeature } from '@/types/brokers/slug';
import FieldsTableFeatures from '@/components/(postTypes)/platforms/(single)/components/FieldsTableFeatures';
import { ExchangeFields } from '@/types/exchanges/page';
import useStoreID from '@/stores/exchangesNumber/store';
import Image from 'next/image';

// Dialog on desktop, bottom sheet on phones; its code loads on the first tap of «بیشتر»
const loadFeaturesSheet = () =>
    import('@/components/(postTypes)/platforms/(single)/components/FeaturesSheet');
const FeaturesSheet = dynamic(loadFeaturesSheet, { ssr: false });

interface BrokerFieldsListProps {
    exchangeField?: ExchangeFields;
    brokerFields?: BrokerFields;
    type: 'foreign' | 'iranian' | 'props' | 'broker' | 'gold' | undefined;
}

type Item = {
    key: string;
    value: any;
    Icon?: React.ComponentType<any>;
};
const BrokerGeneralInfo: React.FC<BrokerFieldsListProps> = ({
    brokerFields,
    exchangeField,
    type,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sheetLoaded, setSheetLoaded] = useState(false);
    // Fetch the sheet's code once the page is idle, so even the first tap opens it at once
    useEffect(() => {
        const preload = () => void loadFeaturesSheet();
        if ('requestIdleCallback' in window) {
            const id = window.requestIdleCallback(preload, { timeout: 4000 });
            return () => window.cancelIdleCallback(id);
        }
        const t = setTimeout(preload, 2500);
        return () => clearTimeout(t);
    }, []);
    const openSheet = () => {
        setSheetLoaded(true);
        setIsModalOpen(true);
    };
    const [countryFlag, setCountryFlag] = useState<string | null>(null);

    const numbers = useStoreID((state) => state.numbers);

    const fetchCountryFlag = useCallback(async (countryName: string) => {
        try {
            const response = await fetch(
                'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/index.json',
            );
            const data = await response.json();
            const country = data.find((item: any) => item.code === countryName);
            if (country) {
                setCountryFlag(country.image); // Set the flag image URL
            }
        } catch (error) {
            console.error('Error fetching country flag:', error);
        }
    }, []);

    useEffect(() => {
        if (exchangeField?.generalInfo.country?.nameEn) {
            fetchCountryFlag(exchangeField?.generalInfo.country.nameEn.toUpperCase()); // Fetch flag for the country
        }
    }, [exchangeField?.generalInfo.country?.nameEn, fetchCountryFlag]);

    let mainItems: Item[] = [];
    let customFeatureItems: CustomFeature[] = [];

    if (type === 'broker') {
        mainItems = [
            {
                key: 'farsiSupport',
                value: brokerFields?.generalInfo.farsiSupport ?? '',
                Icon: FarsiSupportIcon,
            },
            {
                key: 'minimumDeposit',
                value: brokerFields?.generalInfo.minimumDeposit
                    ? brokerFields?.generalInfo.minimumDeposit +
                      ` ${brokerFields?.generalInfo.minimumDepositType ?? 'دلار'}  `
                    : '',
                Icon: Moneys,
            },
            {
                key: 'rialSupport',
                value: brokerFields?.generalInfo.rialSupport ?? '',
                Icon: RialSupportIcon,
            },
            {
                key: 'year',
                value: brokerFields?.generalInfo.year,
                Icon: Calendar,
            },
            {
                key: 'iranSupport',
                value: brokerFields?.generalInfo.iranSupport ?? '',
                Icon: Map,
            },
            {
                key: 'eaUse',
                value: brokerFields?.generalInfo.eaUse ?? '',
                Icon: InfoCircle,
            },
            {
                key: 'farsiSite',
                value: brokerFields?.generalInfo.farsiSite ?? '',
                Icon: InfoCircle,
            },
            {
                key: 'ecnAccount',
                value: brokerFields?.generalInfo.ecnAccount ?? '',
                Icon: InfoCircle,
            },
            {
                key: 'insurance',
                value: brokerFields?.generalInfo.insurance ?? '',
                Icon: InfoCircle,
            },
            {
                key: 'telTrade',
                value: brokerFields?.generalInfo.telTrade ?? '',
                Icon: InfoCircle,
            },
            {
                key: 'identification',
                value: brokerFields?.generalInfo.identification ?? '',
                Icon: InfoCircle,
            },
            {
                key: 'tradeNews',
                value: brokerFields?.generalInfo.tradeNews ?? '',
                Icon: InfoCircle,
            },
            {
                key: 'traditional',
                value: brokerFields?.generalInfo.traditional ?? '',
                Icon: InfoCircle,
            },
            {
                key: 'copyTrading',
                value: brokerFields?.generalInfo.copyTrading ?? '',
                Icon: InfoCircle,
            },
        ];

        customFeatureItems = brokerFields?.generalInfo.customFeature || [];
    } else {
        mainItems = [
            {
                key: 'nameFa',
                value: exchangeField?.generalInfo?.country?.nameFa,
                Icon: InfoCircle,
            },
            {
                key: 'year',
                value: exchangeField?.generalInfo?.year,
                Icon: Calendar,
            },
            {
                key: 'leverageSupport',
                value: exchangeField?.generalInfo?.leverageSupport,
                Icon: Candle,
            },
            {
                key: 'instantAuth',
                value: exchangeField?.generalInfo?.instantAuth,
                Icon: PercentageSquare,
            },
            {
                key: 'coinsNum',
                value:
                    (exchangeField?.generalInfo?.coinsNum || 0 > 0
                        ? exchangeField?.generalInfo.coinsNum
                        : numbers) + '+',
                Icon: Bitcoin,
            },
        ];
        customFeatureItems = exchangeField?.generalInfo?.customFeature || [];
    }

    if (exchangeField?.generalInfo.country.nameEn === null) {
        return null;
    }
    return (
        <div className='space-y-2 border-y dark:border-white/20 py-4 my-3'>
            <ul className='list-none grid-cols-6 items-center justify-center grid gap-x-2 gap-y-4 lg:divide-x lg:divide-x-reverse max-lg:grid-cols-3 max-lg:justify-start'>
                {mainItems.slice(0, 5).map((item, index) => (
                    <li
                        key={item.key}
                        className={`flex flex-col max-lg:basis-2/5 ${index == 1 || index == 4 ? 'max-lg:border-x' : ''} max-lg:aspect-[16/9] dark:border-white/30 items-center justify-center text-sm font-semibold gap-1 max-lg:shrink-0`}
                    >
                        {countryFlag && item.key === 'nameFa' ? (
                            <Image
                                src={countryFlag}
                                alt='Country Flag'
                                width={25}
                                height={25}
                                className='aspect-square object-cover'
                            />
                        ) : item.Icon ? (
                            React.isValidElement(item.Icon) ? (
                                item.Icon
                            ) : (
                                <item.Icon
                                    className='text-progress mb-2 w-[25px] h-[25px]'
                                    size={20}
                                    stroke='currentColor'
                                />
                            )
                        ) : null}

                        <span className={'text-xxs lg:text-xs font-light mb-1 opacity-85'}>
                            {type == 'broker'
                                ? farsiLabels[item.key]
                                : exchangeFarsiLabels[item.key]}
                        </span>
                        <div className={'inline-flex gap-1 items-center'}>
                            {item.value &&
                                typeof item.value !== 'string' &&
                                typeof item.value !== 'number' && (
                                    <TickCircle
                                        size={16}
                                        className={`stroke-white fill-green-500`}
                                    />
                                )}
                            {!item.value && typeof item.value !== 'number' && (
                                <CloseCircle size={16} className={`stroke-white fill-red-500`} />
                            )}
                            <span className={'text-xs text-center'}>
                                {item.value !== 0 && item.value}
                            </span>
                        </div>
                        {/* Display the flag image if countryFlag is available */}
                    </li>
                ))}
                <li
                    role='button'
                    tabIndex={0}
                    aria-haspopup='dialog'
                    onClick={openSheet}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openSheet();
                        }
                    }}
                    className={
                        'flex flex-col items-center space-x-2 max-lg:basis-2/5 max-lg:aspect-[16/9] text-sm font-semibold gap-1 dark:border-white/30 cursor-pointer max-lg:shrink-0 max-lg:px-1'
                    }
                >
                    <Category size={25} className='text-progress mb-2' stroke='currentColor' />
                    <span className={'lg:text-xs text-xxs opacity-85 font-light mb-0.5'}>
                        تمام ویژگی ها
                    </span>
                    <span className={'text-xs text-center'}>بیشتر</span>
                </li>
            </ul>
            {sheetLoaded && (
                <FeaturesSheet open={isModalOpen} onOpenChange={setIsModalOpen}>
                    <FieldsTableFeatures
                        countryFlag={countryFlag}
                        type={type}
                        mainItems={mainItems}
                        customFeatures={customFeatureItems}
                    />
                </FeaturesSheet>
            )}
        </div>
    );
};

export default BrokerGeneralInfo;
